import { useMemo, useState, useCallback } from 'react';
import {
	Box,
	Card,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
} from '@mui/material';
import { Scrollbar } from '../layouts/scrollbar';
import { NodataRow } from './nodata.row';
import Loader from './Loader';

export interface Column {
	key: string;
	title: string;
	formatter?: (column: Column, data: any) => React.ReactNode;
}

interface DataTableProps<T> {
	data: T[];
	columns: Column[];
	loading?: boolean;
	renderRowActions?: (row: T) => React.ReactNode;
	onRowClick?: (row: T) => void;
	minWidth?: number;
}

export const DataTable = <T extends any>(props: DataTableProps<T>) => {
	const {
		data = [],
		columns,
		loading = false,
		renderRowActions,
		onRowClick,
		minWidth = 800,
	} = props;

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const handlePageChange = useCallback((event: any, value: number) => {
		setPage(value);
	}, []);

	const handleRowsPerPageChange = useCallback((event: any) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	}, []);

	const paginatedData = useMemo(() => {
		const startIndex = page * rowsPerPage;
		const endIndex = startIndex + rowsPerPage;
		return data.slice(startIndex, endIndex);
	}, [data, page, rowsPerPage]);

	return (
		<>
			<Loader isLoading={loading} />
			<Card>
				<Scrollbar>
					<Box sx={{ minWidth }}>
						<Table>
							<TableHead>
								<TableRow>
									{columns.map((column: Column) => (
										<TableCell key={column.key}>{column.title}</TableCell>
									))}
									{renderRowActions && <TableCell key="action">Actions</TableCell>}
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedData.length === 0 && (
									<NodataRow colSpan={columns.length + (renderRowActions ? 1 : 0)} />
								)}
								{paginatedData.map((row: any) => (
									<TableRow
										hover
										key={row._id}
										onClick={() => onRowClick?.(row)}
										sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
									>
										{columns.map((column: Column) => (
											<TableCell key={column.key}>
												{column.formatter ? column.formatter(column, row) : row[column.key]}
											</TableCell>
										))}
										{renderRowActions && (
											<TableCell key="action" onClick={(e) => e.stopPropagation()}>
												{renderRowActions(row)}
											</TableCell>
										)}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				</Scrollbar>
				<TablePagination
					component="div"
					count={data.length}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handleRowsPerPageChange}
					page={page}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={[5, 10, 25, 50]}
				/>
			</Card>
		</>
	);
};
